/**
 * The chat: what the user said and what the assistant answered, as pure functions over a value.
 *
 * Rules (docs/NEW-UI/protocollo.md §3):
 *  - a typed message shows at once ("sending"), and the backend's `user_message_ack` confirms it;
 *  - a spoken message appears when the backend has transcribed it (`user_message_ack`, source voice);
 *  - a touch is shown as an event pill with the backend's own sentence for it ("Cerca treni per Milano");
 *  - every reply is shown when it arrives, even after the turn timed out;
 *  - the assistant is "thinking" while a turn is open and has not been answered;
 *  - the reply that closes the last open turn takes the thinking dots' place (`fillsDots`);
 *  - a voice turn the backend took back (`turn_withdrawn`: the user went on) leaves the chat with its reply:
 *    the turn that joins both parts comes next (docs/VOCE/PROGETTO.md §5);
 *  - words from outside the dialogue (`announcement`: a notice, or an operator helping) are a notice with who
 *    says them (docs/VOCE/PROGETTO.md §9).
 */
import type { AssistantMessage, InputSource } from './wire';

export type EntryStatus = 'sending' | 'sent' | 'failed';

export interface UserEntry {
  role: 'user';
  id: string;
  text: string;
  source: InputSource;
  turnId: string | null;
  status: EntryStatus;
  at: number;
}

export interface AssistantEntry {
  role: 'assistant';
  id: string;
  text: string;
  turnId: string;
  at: number;
  /** The whole message, for the dev chips and the trace viewer. */
  message: AssistantMessage;
  /** It closed the last open turn: its words fill the bubble that had the thinking dots, no new one rises. */
  fillsDots: boolean;
}

export interface NoticeEntry {
  role: 'notice';
  id: string;
  /** i18n key, or free text from the backend (`text`). */
  key?: string;
  text?: string;
  /** i18n key of who says it, shown before the text: words from outside the dialogue (an operator, a notice). */
  label?: string;
  tone: 'info' | 'warn' | 'error';
  at: number;
}

export type ChatEntry = UserEntry | AssistantEntry | NoticeEntry;

export interface Conversation {
  entries: ChatEntry[];
  /** Turns sent and not answered yet, with when they were opened. */
  openTurns: Record<string, number>;
  /** Turns answered before their POST returned (the reply can beat the 202). */
  answeredTurns: string[];
}

export const emptyConversation: Conversation = { entries: [], openTurns: {}, answeredTurns: [] };

const MAX_ENTRIES = 200;
const MAX_ANSWERED = 50;
let seq = 0;
const nextId = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${(seq++).toString(36)}`;

function append(c: Conversation, entry: ChatEntry): Conversation {
  return { ...c, entries: [...c.entries, entry].slice(-MAX_ENTRIES) };
}

/** The user typed something: show it now. Returns the entry id, to mark it failed if the POST fails. */
export function typed(c: Conversation, text: string, at = Date.now()): { conversation: Conversation; entryId: string } {
  const entryId = nextId('u');
  const entry: UserEntry = { role: 'user', id: entryId, text, source: 'text', turnId: null, status: 'sending', at };
  return { conversation: append(c, entry), entryId };
}

export function turnOpened(c: Conversation, turnId: string, at = Date.now()): Conversation {
  if (c.answeredTurns.includes(turnId)) return c;
  return { ...c, openTurns: { ...c.openTurns, [turnId]: at } };
}

export function sendFailed(c: Conversation, entryId: string | null, key: string, at = Date.now()): Conversation {
  const entries = c.entries.map((e) => (e.role === 'user' && e.id === entryId ? { ...e, status: 'failed' as const } : e));
  return append({ ...c, entries }, { role: 'notice', id: nextId('n'), key, tone: 'error', at });
}

export function userSaid(c: Conversation, turnId: string, text: string, source: InputSource, at = Date.now()): Conversation {
  const withTurn = turnOpened(c, turnId, at);
  if (withTurn.entries.some((e) => e.role === 'user' && e.turnId === turnId)) return withTurn;
  const pending = withTurn.entries.findIndex((e) => e.role === 'user' && e.status === 'sending' && e.text === text);
  if (pending !== -1 && source !== 'touch') {
    const entries = [...withTurn.entries];
    entries[pending] = { ...(entries[pending] as UserEntry), turnId, status: 'sent' };
    return { ...withTurn, entries };
  }
  const entry: UserEntry = { role: 'user', id: nextId('u'), text, source, turnId, status: 'sent', at };
  return append(withTurn, entry);
}

export function replied(c: Conversation, message: AssistantMessage, at = Date.now()): Conversation {
  const { [message.turn_id]: _closed, ...openTurns } = c.openTurns;
  const answeredTurns = [...c.answeredTurns, message.turn_id].slice(-MAX_ANSWERED);
  const next = { ...c, openTurns, answeredTurns };
  if (!message.text.trim()) return next; // a silent touch ack: nothing to say
  const fillsDots = message.turn_id in c.openTurns && !isThinking(next);
  return append(next, { role: 'assistant', id: nextId('a'), text: message.text, turnId: message.turn_id, at, message, fillsDots });
}

export function turnFailed(c: Conversation, turnId: string, text: string, at = Date.now()): Conversation {
  const { [turnId]: _closed, ...openTurns } = c.openTurns;
  return append({ ...c, openTurns }, { role: 'notice', id: nextId('n'), text, key: text ? undefined : 'chat.turn_failed', tone: 'error', at });
}

export function timedOut(c: Conversation, now: number, timeoutMs: number): Conversation {
  const late = Object.entries(c.openTurns).filter(([, at]) => now - at >= timeoutMs).map(([id]) => id);
  if (late.length === 0) return c;
  const openTurns = Object.fromEntries(Object.entries(c.openTurns).filter(([id]) => !late.includes(id)));
  return append({ ...c, openTurns }, { role: 'notice', id: nextId('n'), key: 'chat.timeout', tone: 'warn', at: now });
}

export function noticed(c: Conversation, notice: { key?: string; text?: string; label?: string; tone: NoticeEntry['tone'] }, at = Date.now()): Conversation {
  return append(c, { role: 'notice', id: nextId('n'), ...notice, at });
}

/**
 * The last voice exchange leaves the chat: the user's words `text` (the latest spoken entry, when it says them) and
 * everything after it. The backend sends this right after that turn and before the turn that replaces it.
 */
export function withdrawn(c: Conversation, text: string): Conversation {
  const at = c.entries.findLastIndex((e) => e.role === 'user' && e.source === 'voice');
  const entry = c.entries[at] as UserEntry | undefined;
  if (!entry || (text && entry.text.trim() !== text.trim())) return c;
  const gone = new Set(c.entries.slice(at).flatMap((e) => (e.role === 'notice' ? [] : [e.turnId])));
  const openTurns = Object.fromEntries(Object.entries(c.openTurns).filter(([id]) => !gone.has(id)));
  return { ...c, entries: c.entries.slice(0, at), openTurns };
}

export function isThinking(c: Conversation): boolean {
  return Object.keys(c.openTurns).length > 0;
}
